import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, Save, AlertCircle, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const AnalyticsCRUD = ({ config, onUpdate }) => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // Get authentication headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (config.analytics) {
      setAnalyticsData(config.analytics);
    }
  }, [config]);

  const handleInputChange = (key, value) => {
    setAnalyticsData(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
    setSaveStatus('');
  };

  const saveAnalyticsSettings = async () => {
    setLoading(true);
    setSaveStatus('');
    
    try {
      // Save each analytics setting
      for (const [key, value] of Object.entries(analyticsData)) {
        await axios.put(`${API_BASE_URL}/api/admin/config/analytics/${key}`, {
          value: value,
          data_type: typeof value === 'object' ? 'json' : 'string'
        }, {
          headers: getAuthHeaders()
        });
      }

      // Reload config data first
      await onUpdate();
      
      // Then update UI state after fresh data is loaded
      setHasUnsavedChanges(false);
      setSaveStatus('success');
      
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving analytics settings:', error);
      setSaveStatus('error');
      if (error.response?.status === 401) {
        alert('Authentication required. Please log in again.');
      } else {
        alert('Error saving analytics settings. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const discardChanges = () => {
    if (hasUnsavedChanges && window.confirm('Are you sure you want to discard your changes?')) {
      setAnalyticsData(config.analytics || {});
      setHasUnsavedChanges(false);
      setSaveStatus('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <BarChart3 className="h-5 w-5 text-blue-600 mr-2" />
          <h4 className="text-lg font-semibold text-blue-900">Analytics Configuration</h4>
        </div>
        <p className="text-blue-700 mt-2">
          Configure analytics and tracking settings for your website.
        </p>
      </div>

      {/* Save Status */}
      {saveStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800 font-medium">Analytics settings saved successfully!</span>
          </div>
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">Error saving analytics settings. Please try again.</span>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">You have unsaved changes</h4>
                <p className="text-sm text-yellow-700">Save your changes to update the analytics configuration.</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={discardChanges}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                disabled={loading}
              >
                Discard
              </button>
              <button
                onClick={saveAnalyticsSettings}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center space-x-2"
                disabled={loading}
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Settings Form */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h5 className="font-semibold text-gray-900 mb-6">Analytics Settings</h5>
        
        <div className="space-y-6">
          {/* Google Analytics */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Analytics Tracking ID
            </label>
            <input
              type="text"
              value={analyticsData.google_analytics_id || ''}
              onChange={(e) => handleInputChange('google_analytics_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="GA-XXXXXXXXX-X or G-XXXXXXXXXX"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter your Google Analytics tracking ID to enable website analytics.
            </p>
          </div>

          {/* Google Tag Manager */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Tag Manager ID
            </label>
            <input
              type="text"
              value={analyticsData.gtm_id || ''}
              onChange={(e) => handleInputChange('gtm_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="GTM-XXXXXXX"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter your Google Tag Manager ID for advanced tracking configuration.
            </p>
          </div>

          {/* Facebook Pixel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facebook Pixel ID
            </label>
            <input
              type="text"
              value={analyticsData.facebook_pixel_id || ''}
              onChange={(e) => handleInputChange('facebook_pixel_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="123456789012345"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter your Facebook Pixel ID for social media tracking and advertising.
            </p>
          </div>

          {/* LinkedIn Insight Tag */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              LinkedIn Insight Tag Partner ID
            </label>
            <input
              type="text"
              value={analyticsData.linkedin_partner_id || ''}
              onChange={(e) => handleInputChange('linkedin_partner_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="1234567"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">
              Enter your LinkedIn Insight Tag Partner ID for B2B tracking.
            </p>
          </div>

          {/* Custom Analytics Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Analytics Code
            </label>
            <textarea
              value={analyticsData.custom_analytics_code || ''}
              onChange={(e) => handleInputChange('custom_analytics_code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={6}
              placeholder="<!-- Add custom analytics tracking code here -->"
              disabled={loading}
            />
            <p className="text-sm text-gray-500 mt-1">
              Add any custom analytics or tracking code that should be included in the website header.
            </p>
          </div>

          {/* Analytics Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={analyticsData.enable_analytics || false}
                  onChange={(e) => handleInputChange('enable_analytics', e.target.checked)}
                  className="rounded text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">Enable Analytics Tracking</span>
              </label>
            </div>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={analyticsData.enable_cookie_consent || false}
                  onChange={(e) => handleInputChange('enable_cookie_consent', e.target.checked)}
                  className="rounded text-primary-600 focus:ring-primary-500"
                  disabled={loading}
                />
                <span className="ml-2 text-sm text-gray-700">Enable Cookie Consent Banner</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCRUD;